import { TenderState } from '../types/tender.types';

export interface ErrorInfo {
  type: 'calculation' | 'validation' | 'network' | 'storage' | 'unexpected';
  severity: 'low' | 'medium' | 'high' | 'critical';
  message: string;
  details?: string;
  timestamp: Date;
  userAction?: string;
  stackTrace?: string;
  context?: Record<string, any>;
}

export interface RecoveryAction {
  label: string;
  action: () => void;
  type: 'primary' | 'secondary';
}

export class ErrorHandler {
  private static errorLog: ErrorInfo[] = [];
  private static maxLogSize = 50;
  
  /**
   * Log error with context
   */
  static logError(error: ErrorInfo): void {
    this.errorLog.unshift({
      ...error,
      timestamp: new Date()
    });
    
    // Keep log size manageable
    if (this.errorLog.length > this.maxLogSize) {
      this.errorLog = this.errorLog.slice(0, this.maxLogSize);
    }
    
    // Store in localStorage for debugging
    try {
      localStorage.setItem('mj-estimator-error-log', JSON.stringify(this.errorLog));
    } catch (e) {
      // Storage full or unavailable
      console.warn('Could not store error log:', e);
    }
    
    // Send to monitoring service in production
    if (import.meta.env.MODE === 'production' && error.severity === 'critical') {
      this.sendToMonitoring(error);
    }
  }
  
  /**
   * Handle auto-save failure
   */
  static handleAutoSaveFail(
    error: Error, 
    data: TenderState,
    showToast: (message: string, type: 'warning' | 'error') => void
  ): void {
    const errorInfo: ErrorInfo = {
      type: 'network',
      severity: 'medium',
      message: 'Auto-save failed - your work is preserved locally',
      details: error.message,
      timestamp: new Date(),
      userAction: 'auto-save',
      context: { projectName: data.projectName, dataSize: JSON.stringify(data).length }
    };
    
    this.logError(errorInfo);
    
    // Store in session storage as backup
    try {
      sessionStorage.setItem('mj-estimator-backup', JSON.stringify({
        data,
        timestamp: new Date().toISOString(),
        reason: 'auto-save-failure'
      }));
      
      showToast('Auto-save failed. Your work is preserved locally.', 'warning');
      
      // Set retry timer
      setTimeout(() => {
        this.retryAutoSave(data, showToast);
      }, 60000); // Retry in 30 seconds
      
    } catch (storageError) {
      showToast('Auto-save failed and local backup failed. Please save manually.', 'error');
      
      this.logError({
        type: 'storage',
        severity: 'high',
        message: 'Session storage backup failed',
        details: (storageError as Error).message,
        timestamp: new Date(),
        context: { originalError: error.message }
      });
    }
  }
  
  /**
   * Handle calculation errors
   */
  static handleCalculationError(
    error: Error,
    stage: string,
    data: any,
    onPartialSuccess?: (partialResults: any) => void
  ): { canContinue: boolean; partialResults?: any; recoveryActions: RecoveryAction[] } {
    const errorInfo: ErrorInfo = {
      type: 'calculation',
      severity: 'medium',
      message: `Calculation failed at stage: ${stage}`,
      details: error.message,
      timestamp: new Date(),
      userAction: 'calculate',
      context: { stage, dataSnapshot: this.sanitizeData(data) }
    };
    
    this.logError(errorInfo);
    
    // Try to recover partial results
    const partialResults = this.attemptPartialCalculation(stage, data);
    
    const recoveryActions: RecoveryAction[] = [
      {
        label: 'Retry Calculation',
        action: () => {
          // Trigger recalculation
          window.dispatchEvent(new CustomEvent('retry-calculation', { detail: { stage, data } }));
        },
        type: 'primary'
      },
      {
        label: 'Skip This Section',
        action: () => {
          if (onPartialSuccess && partialResults) {
            onPartialSuccess(partialResults);
          }
        },
        type: 'secondary'
      }
    ];
    
    return {
      canContinue: !!partialResults,
      partialResults,
      recoveryActions
    };
  }
  
  /**
   * Handle validation errors
   */
  static handleValidationError(
    validationErrors: string[],
    sectionName: string,
    _data: any
  ): RecoveryAction[] {
    const errorInfo: ErrorInfo = {
      type: 'validation',
      severity: 'low',
      message: `Validation failed in ${sectionName}`,
      details: validationErrors.join('; '),
      timestamp: new Date(),
      userAction: 'validate',
      context: { section: sectionName, errorCount: validationErrors.length }
    };
    
    this.logError(errorInfo);
    
    return [
      {
        label: 'Fix Issues',
        action: () => {
          // Focus on first error field
          const firstErrorField = document.querySelector('[data-error="true"]') as HTMLElement;
          firstErrorField?.focus();
        },
        type: 'primary'
      },
      {
        label: 'Continue Anyway',
        action: () => {
          window.dispatchEvent(new CustomEvent('override-validation', { 
            detail: { section: sectionName } 
          }));
        },
        type: 'secondary'
      }
    ];
  }
  
  /**
   * Handle unexpected errors with crash recovery
   */
  static handleUnexpectedError(
    error: Error,
    componentStack?: string,
    errorBoundary?: string
  ): void {
    const errorInfo: ErrorInfo = {
      type: 'unexpected',
      severity: 'critical',
      message: 'Unexpected application error',
      details: error.message,
      timestamp: new Date(),
      stackTrace: error.stack,
      context: { 
        componentStack, 
        errorBoundary,
        url: window.location.href,
        userAgent: navigator.userAgent
      }
    };
    
    this.logError(errorInfo);
    
    // Attempt crash recovery
    this.attemptCrashRecovery();
  }
  
  /**
   * Retry auto-save operation
   */
  private static async retryAutoSave(
    data: TenderState,
    showToast: (message: string, type: 'error' | 'warning') => void
  ): Promise<void> {
    try {
      // Implement your auto-save logic here
      await this.saveToServer(data);
      showToast('Auto-save recovered successfully', 'warning');
      
      // Clear backup after successful save
      sessionStorage.removeItem('mj-estimator-backup');
      
    } catch (error) {
      // Retry failed, schedule another retry
      setTimeout(() => {
        this.retryAutoSave(data, showToast);
      }, 60000); // Retry in 1 minute
    }
  }
  
  /**
   * Attempt partial calculation recovery
   */
  private static attemptPartialCalculation(stage: string, data: any): any {
    try {
      // Implement partial calculation logic based on stage
      switch (stage) {
        case 'foul-sewers':
          return this.calculateFoulSewersPartial(data);
        case 'storm-sewers':
          return this.calculateStormSewersPartial(data);
        case 'roads':
          return this.calculateRoadsPartial(data);
        default:
          return null;
      }
    } catch (error) {
      return null;
    }
  }
  
  /**
   * Attempt crash recovery
   */
  private static attemptCrashRecovery(): void {
    try {
      // Check for backup data
      const backup = sessionStorage.getItem('mj-estimator-backup');
      if (backup) {
        const backupData = JSON.parse(backup);
        
        // Restore data
        window.dispatchEvent(new CustomEvent('restore-from-backup', { 
          detail: backupData 
        }));
      }
      
      // Clear any corrupt localStorage data
      this.clearCorruptData();
      
    } catch (error) {
      // Complete reset required
      this.performCompleteReset();
    }
  }
  
  /**
   * Get recovery suggestions based on error history
   */
  static getRecoverySuggestions(): string[] {
    const recentErrors = this.errorLog.slice(0, 10);
    const suggestions: string[] = [];
    
    // Analyze error patterns
    const errorTypes = recentErrors.reduce((acc, error) => {
      acc[error.type] = (acc[error.type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    if (errorTypes.network > 3) {
      suggestions.push('Check your internet connection and try again');
    }
    
    if (errorTypes.calculation > 2) {
      suggestions.push('Verify your input data for any unusual values');
    }
    
    if (errorTypes.storage > 1) {
      suggestions.push('Clear browser cache and local storage');
    }
    
    if (errorTypes.validation > 5) {
      suggestions.push('Review data validation requirements');
    }
    
    return suggestions;
  }
  
  /**
   * Export error log for debugging
   */
  static exportErrorLog(): string {
    return JSON.stringify({
      errors: this.errorLog,
      browserInfo: {
        userAgent: navigator.userAgent,
        url: window.location.href,
        timestamp: new Date().toISOString()
      }
    }, null, 2);
  }
  
  // Private helper methods
  private static sanitizeData(data: any): any {
    // Remove sensitive information from error logs
    const sanitized = { ...data };
    
    // Remove any potential PII
    if (sanitized.client) {
      sanitized.client = sanitized.client.substring(0, 3) + '***';
    }
    
    return sanitized;
  }
  
  private static async saveToServer(data: TenderState): Promise<void> {
    // Implement your server save logic here
    const response = await fetch('/api/tenders/save', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      throw new Error(`Save failed: ${response.statusText}`);
    }
  }
  
  private static sendToMonitoring(error: ErrorInfo): void {
    // Implement monitoring service integration
    fetch('/api/monitoring/error', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(error)
    }).catch(() => {
      // Monitoring failed, store locally for later retry
      console.warn('Monitoring service unavailable');
    });
  }
  
  private static calculateFoulSewersPartial(_data: any): any {
    // Implement partial foul sewers calculation
    return { partial: true, section: 'foul-sewers' };
  }
  
  private static calculateStormSewersPartial(_data: any): any {
    // Implement partial storm sewers calculation
    return { partial: true, section: 'storm-sewers' };
  }
  
  private static calculateRoadsPartial(_data: any): any {
    // Implement partial roads calculation
    return { partial: true, section: 'roads' };
  }
  
  private static clearCorruptData(): void {
    try {
      // Clear potentially corrupt localStorage items
      const keysToRemove = Object.keys(localStorage).filter(key => 
        key.startsWith('mj-estimator-')
      );
      
      keysToRemove.forEach(key => {
        try {
          localStorage.removeItem(key);
        } catch (error) {
          // Individual key removal failed
        }
      });
    } catch (error) {
      // Complete localStorage clear
      localStorage.clear();
    }
  }
  
  private static performCompleteReset(): void {
    // Last resort - complete application reset
    localStorage.clear();
    sessionStorage.clear();
    
    // Redirect to fresh start
    window.location.href = window.location.origin + window.location.pathname;
  }
}

// Global error event listener
window.addEventListener('error', (event) => {
  ErrorHandler.handleUnexpectedError(event.error);
});

// Unhandled promise rejection listener
window.addEventListener('unhandledrejection', (event) => {
  ErrorHandler.handleUnexpectedError(
    new Error(event.reason), 
    undefined, 
    'unhandled-promise-rejection'
  );
});

export default ErrorHandler;